import {TitleCasePipe} from '@angular/common'
import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {Router} from '@angular/router'
import {IonButton, IonIcon, IonSpinner, ModalController, NavController} from '@ionic/angular/standalone'
import {Store} from '@ngrx/store'
import {PreferencesStorageIds} from '@common/global-constants'
import {WarningAlertColor} from '@models/enums/warning-alert-message.enum'
import {Outlet} from '@models/outlet'
import {SyncLocalInfo} from '@models/sync.models'
import {getStaticOutletsPlannedSelector} from '@pages/home/store/home.selectors'
import {UveTranslatePipe} from '@pipes/uve-translate.pipe'
import {AuthService} from '@service/auth/auth.service'
import {ExtensionsService} from '@service/extensions-points/extensions.service'
import {HomeSearchCommunicationService} from '@service/home-search-communication.service'
import {KeepAwakeService} from '@service/keep-awake.service'
import {NetworkService} from '@service/network.service'
import {OutletService} from '@service/outlet/outlet.service'
import {PermissionsService} from '@service/permissions/permissions.service'
import {PreferencesApiService} from '@service/storage/preferences-api.service'
import {RestSyncService} from '@service/sync/rest-sync.service'
import {SyncService} from '@service/sync/sync.service'
import {UveLogKey} from '@models/constants/log-key'
import {LoggingService} from '@service/logging.service'
import {ModalHeaderComponent} from '../modal-header/modal-header.component'
import {ButtonActionComponent} from '../shared/button-action/button-action.component'
import {WarningAlertMessageComponent} from '../warning-alert-message/warning-alert-message.component'

@Component({
  selector: 'app-logout-modal',
  imports: [
    IonButton,
    IonIcon,
    IonSpinner,
    ModalHeaderComponent,
    WarningAlertMessageComponent,
    ButtonActionComponent,
    TitleCasePipe,
    UveTranslatePipe,
  ],
  standalone: true,
  templateUrl: './logout-modal.component.html',
  styleUrl: './logout-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogoutModalComponent implements OnInit {
  syncDone = false
  syncError = false
  callingLogoutAction = false
  openActivities = signal(0)
  staticPlannedOutlets: Outlet[] = []
  WarningAlertColor = WarningAlertColor
  isSyncing = signal(false)

  private readonly destroyRef = inject(DestroyRef)
  private readonly extensionsService = inject(ExtensionsService)
  private readonly router = inject(Router)
  private readonly navController = inject(NavController)
  private readonly modalController = inject(ModalController)
  private readonly permissions = inject(PermissionsService)
  private readonly authService = inject(AuthService)
  private readonly loggingService = inject(LoggingService)
  protected readonly syncRestService = inject(RestSyncService)
  protected readonly networkService = inject(NetworkService)
  protected readonly syncService = inject(SyncService)
  private readonly preferencesApiService = inject(PreferencesApiService)
  private readonly store = inject(Store)
  private readonly outletService = inject(OutletService)
  private readonly homeSearchCommunicationService = inject(HomeSearchCommunicationService)
  private readonly keepAwake = inject(KeepAwakeService)

  ngOnInit(): void {
    this.init()
  }

  init = async (): Promise<void> => {
    this.syncError = false
    this.store
      .select(getStaticOutletsPlannedSelector)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.staticPlannedOutlets = res
        this.countOpenActivities()
      })
  }

  private readonly countOpenActivities = async (): Promise<void> => {
    const count = await this.outletService.countOpenActivities(this.staticPlannedOutlets)
    this.openActivities.set(count)
  }

  close = (): void => {
    this.loggingService.debug(UveLogKey.Debug.CloseLogoutModal)
    this.modalController.dismiss()
  }

  logout = async (): Promise<void> => {
    if (!this.networkService.isOnline()) {
      return
    }
    this.callingLogoutAction = true
    await this.authService.logout()
    this.callingLogoutAction = false
    this.extensionsService.clearStorageExtensionTrace()
    this.modalController.dismiss()
  }

  sync = async (): Promise<void> => {
    if (!this.networkService.isOnline()) {
      return
    }
    this.isSyncing.set(true)
    await this.loggingService.debug(UveLogKey.Debug.StartSyncLogoutModal)
    this.syncRestService.syncText = 'sending_data'
    this.syncRestService.synchronizing = true

    await this.keepAwake.keepAwake()

    await this.preferencesApiService
      .get<SyncLocalInfo>(PreferencesStorageIds.SYNC_INFO)
      .then((syncInfo: SyncLocalInfo) => {
        syncInfo.last = {startDate: new Date()}
        syncInfo.lastSyncUpDate = new Date()
        syncInfo.lastSyncDownDate = new Date()
        syncInfo.initial = false
        this.preferencesApiService.set(PreferencesStorageIds.SYNC_INFO, syncInfo)
      })

    try {
      await this.syncService.uploadSync()
    } catch {
      this.syncError = true
    }

    this.syncDone = true
    this.syncRestService.synchronizing = false
    this.syncRestService.resetPercentages()
    this.syncRestService.syncText = ''

    await this.extensionsService.clearStorageExtensionTrace()

    await this.loggingService.debug(UveLogKey.Debug.EndSyncLogoutModal)
    await this.keepAwake.allowSleep()
    this.isSyncing.set(false)
  }

  showOpenActivities = async (): Promise<void> => {
    const currentUrl = this.router.url.split('?')[0]
    const isInHome = currentUrl === '/home' || currentUrl.startsWith('/home/')

    if (isInHome) {
      this.applyOpenActivitiesFilter()
      await this.modalController.dismiss()
      return
    }

    await this.modalController.dismiss()
    await this.navController.navigateRoot('/home', {
      animated: true,
      state: {applyOpenActivitiesFilter: true},
    })
  }

  private readonly applyOpenActivitiesFilter = (): void => {
    this.homeSearchCommunicationService.setSearchState(this.outletService.filtersOpenActivities)
  }
}
